import { Scalar } from "../math/math";
import { IsEnvelope, IsGeoBounded, IsLocation } from "./geography.interfaces";
import { Geo3 } from "./geography.position";
import { Size3 } from "../geometry/geometry.size";
export class Envelope {
    static RegionIntersectsRegion(a, b) {
        if (a[1] > b[3] || a[3] < b[1] || a[0] > b[2] || a[2] < b[0] || a[4] > b[5] || a[5] < b[4]) {
            return false;
        }
        return true;
    }
    static RegionContainsFloat(a, lat, lon, alt) {
        return lat >= a[1] && lat <= a[3] && (lon === undefined || (lon >= a[0] && lon <= a[2])) && (alt === undefined || (alt >= a[4] && alt <= a[5]));
    }
    static Zero() {
        return new Envelope(Geo3.Zero(), Geo3.Zero());
    }
    static Split2(a) {
        if (a) {
            if (IsGeoBounded(a)) {
                return Envelope.Split2(a.geoBounds);
            }
            const center = a.center;
            return [
                new Envelope(new Geo3(center.lat, a.west), new Geo3(a.north, center.lon)),
                new Envelope(center, new Geo3(a.north, a.east)),
                new Envelope(new Geo3(a.south, a.west), center),
                new Envelope(new Geo3(a.south, center.lon), new Geo3(center.lat, a.east)),
            ];
        }
        return [];
    }
    static Split3(a) {
        if (a) {
            if (IsGeoBounded(a)) {
                return Envelope.Split3(a.geoBounds);
            }
            if (a.hasAltitude) {
                const center = a.center;
                return [
                    new Envelope(new Geo3(center.lat, a.west, a.bottom), new Geo3(a.north, center.lon, center.alt)),
                    new Envelope(new Geo3(center.lat, center.lon, a.bottom), new Geo3(a.north, a.east, center.alt)),
                    new Envelope(new Geo3(a.south, a.west, a.bottom), center),
                    new Envelope(new Geo3(a.south, center.lon, a.bottom), new Geo3(center.lat, a.east, center.alt)),
                    new Envelope(new Geo3(center.lat, a.west, center.alt), new Geo3(a.north, center.lon, a.top)),
                    new Envelope(new Geo3(center.lat, center.lon, center.alt), new Geo3(a.north, a.east, a.top)),
                    new Envelope(new Geo3(a.south, a.west, center.alt), new Geo3(center.lat, center.lon, a.top)),
                    new Envelope(new Geo3(a.south, center.lon, center.alt), new Geo3(center.lat, a.east, a.top)),
                ];
            }
        }
        return [];
    }
    static FromPoints(...array) {
        const a = array[0];
        const hasAlt = a.alt !== undefined && a.alt !== undefined;
        const lat0 = Scalar.Clamp(a.lat, Envelope.MinLatitude, Envelope.MaxLatitude);
        const lon0 = Scalar.Clamp(a.lon, Envelope.MinLongitude, Envelope.MaxLongitude);
        const alt0 = hasAlt ? a.alt : undefined;
        const env = new Envelope(new Geo3(lat0, lon0, alt0), new Geo3(lat0, lon0, alt0));
        for (let i = 1; i < array.length; i++) {
            env.addInPlace(array[i]);
        }
        return env;
    }
    static FromEnvelopes(...array) {
        let env = undefined;
        for (let i = 0; i < array.length; i++) {
            let a = array[i];
            if (a) {
                if (IsEnvelope(a)) {
                    env = env ? env.unionInPlace(a) : a.clone();
                }
                else {
                    a = a.geoBounds;
                    if (a) {
                        env = env ? env.unionInPlace(a) : a.clone();
                    }
                }
            }
        }
        return env;
    }
    constructor(lowerCorner, upperCorner) {
        this._min = lowerCorner;
        this._max = upperCorner;
    }
    isEmpty() {
        return this._min.equals(this._max);
    }
    get north() {
        return this._max.lat;
    }
    get south() {
        return this._min.lat;
    }
    get east() {
        return this._max.lon;
    }
    get west() {
        return this._min.lon;
    }
    get bottom() {
        return this._min.alt;
    }
    get top() {
        return this._max.alt;
    }
    get nw() {
        return new Geo3(this.north, this.west);
    }
    get sw() {
        return new Geo3(this.south, this.west);
    }
    get ne() {
        return new Geo3(this.north, this.east);
    }
    get se() {
        return new Geo3(this.south, this.east);
    }
    equals(other) {
        return (other !== undefined &&
            this._min.lat === other.south &&
            this._min.lon === other.west &&
            this._min.alt === other.bottom &&
            this._max.lat === other.north &&
            this._max.lon === other.east &&
            this._max.alt === other.top);
    }
    clone() {
        return new Envelope(this._min.clone(), this._max.clone());
    }
    get hasAltitude() {
        return this._min.hasAltitude && this._max.hasAltitude;
    }
    get center() {
        const lon = this._min.lon + (this._max.lon - this._min.lon) / 2;
        const lat = this._min.lat + (this._max.lat - this._min.lat) / 2;
        const alt = this.hasAltitude ? this._min.alt + (this._max.alt - this._min.alt) / 2 : undefined;
        return new Geo3(lat, lon, alt);
    }
    get size() {
        const w = this._max.lon - this._min.lon;
        const h = this._max.lat - this._min.lat;
        const t = this.hasAltitude ? this._max.alt - this._min.alt : 0;
        return new Size3(w, h, t);
    }
    add(lat, lon, alt) {
        return this.clone().addInPlace(lat, lon, alt);
    }
    addInPlace(lat, lon, alt) {
        if (IsLocation(lat)) {
            return this.addInPlace(lat.lat, lat.lon, lat.alt);
        }
        this._min.lat = Math.min(this._min.lat, lat);
        this._max.lat = Math.max(this._max.lat, lat);
        if (lon !== undefined) {
            this._min.lon = Math.min(this._min.lon, lon);
            this._max.lon = Math.max(this._max.lon, lon);
        }
        if (this.hasAltitude && alt) {
            this._min.alt = Math.min(this._min.alt, alt);
            this._max.alt = Math.max(this._max.alt, alt);
        }
        return this;
    }
    unionInPlace(other) {
        this._min.lat = Math.min(this._min.lat, other.south);
        this._max.lat = Math.max(this._max.lat, other.north);
        this._min.lon = Math.min(this._min.lon, other.west);
        this._max.lon = Math.max(this._max.lon, other.east);
        if (this.hasAltitude && other.hasAltitude) {
            this._min.alt = Math.min(this._min.alt, other.bottom);
            this._max.alt = Math.max(this._max.alt, other.top);
        }
        return this;
    }
    intersects(bounds) {
        if (bounds === undefined)
            return false;
        if (Array.isArray(bounds)) {
            if (this._min.lat > bounds[3] || this._max.lat < bounds[1] || this._min.lon > bounds[2] || this._max.lon < bounds[0]) {
                return false;
            }
            if (this.hasAltitude && bounds.length === 6) {
                if (this._min.alt > bounds[5] || this._max.alt < bounds[4]) {
                    return false;
                }
            }
            return true;
        }
        if (IsGeoBounded(bounds)) {
            return this.intersects(bounds.geoBounds);
        }
        if (this._min.lat > bounds.north || this._max.lat < bounds.south || this._min.lon > bounds.east || this._max.lon < bounds.west) {
            return false;
        }
        if (this.hasAltitude && bounds.hasAltitude) {
            if (this._min.alt > bounds.top || this._max.alt < bounds.bottom) {
                return false;
            }
        }
        return true;
    }
    contains(other) {
        if (!other)
            return false;
        if (IsLocation(other)) {
            return this.containsFloat(other.lat, other.lon, other.alt);
        }
        return (this.containsFloat(other.south, other.west) &&
            this.containsFloat(other.north, other.east) &&
            (!this.hasAltitude ||
                (other.bottom !== undefined &&
                    other.top !== undefined &&
                    this.containsFloat(other.bottom, undefined, other.bottom) &&
                    this.containsFloat(other.top, undefined, other.top))));
    }
    containsFloat(lat, lon, alt) {
        return (lat >= this._min.lat &&
            lat <= this._max.lat &&
            (lon === undefined || (lon >= this._min.lon && lon <= this._max.lon)) &&
            (alt === undefined || (this.hasAltitude && alt >= this._min.alt && alt <= this._max.alt)));
    }
    scaleInPlace(scale) {
        const size = this.size;
        size.width = (size.width * scale - size.width) / 2;
        size.height = (size.height * scale - size.height) / 2;
        size.depth = (size.depth * scale - size.depth) / 2;
        this._min.lat -= size.height;
        this._min.lon -= size.width;
        if (this._min.alt) {
            this._min.alt -= size.depth;
        }
        this._max.lat += size.height;
        this._max.lon += size.width;
        if (this._max.alt) {
            this._max.alt += size.depth;
        }
        return this;
    }
    toString() {
        return `Envelope(${this._min.toString()}, ${this._max.toString()})`;
    }
}
Envelope.MaxLongitude = 540;
Envelope.MaxLatitude = 90;
Envelope.MinLongitude = -Envelope.MaxLongitude;
Envelope.MinLatitude = -Envelope.MaxLatitude;
export class GeoBounded {
    constructor(bounds, parent) {
        if (parent) {
            this._parent = parent;
        }
        this._env = bounds;
    }
    get parent() {
        return this._parent;
    }
    get geoBounds() {
        this.validateEnvelope();
        return this._env;
    }
    validateEnvelope() {
        if (!this._env) {
            this._env = this._buildEnvelope();
        }
    }
    invalidateEnvelope() {
        if (this._env) {
            delete this._env;
            if (this._parent) {
                this._parent.invalidateEnvelope();
            }
        }
    }
}
//# sourceMappingURL=geography.envelope.js.map